import { useSelector } from 'react-redux'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClearBrowserCache } from 'react-clear-browser-cache'
import { locationSelector } from 'app-redux/selectors'
import {
  APP_UPDATE_EVENT,
  fetchLatestAppVersion,
  isSafeToReloadPath,
  reloadToVersion,
} from 'utils/appUpdate'
import { history } from '@/store'

// Jeda minimal antar pengecekan versi saat tab kembali aktif
const VISIBLE_CHECK_THROTTLE = 60000
// Nilai placeholder react-clear-browser-cache saat versi belum/tidak bisa disimpan
// (localStorage diblokir). Membandingkan dengan nilai ini akan selalu "beda" → reload terus.
const UNKNOWN_VERSIONS = ['latest', 'disabled']

// Hook untuk handle jika ada update dari server.
// Tidak pernah reload di halaman yang sedang dibuka: versi baru dipasang saat user pindah
// ke halaman aman, atau saat user sendiri menekan tombol muat ulang.
const useAppUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const { isLatestVersion, latestVersion } = useClearBrowserCache()
  const pendingVersionRef = useRef(null)
  const lastVisibleCheckRef = useRef(0)

  const location = useSelector(locationSelector)

  const isSafeToReload = useMemo(() => isSafeToReloadPath(location.pathname), [location.pathname])

  const markUpdateAvailable = useCallback((version) => {
    if (version) {
      pendingVersionRef.current = version
    }
    setUpdateAvailable(true)
  }, [])

  const reloadPage = useCallback(() => {
    reloadToVersion(pendingVersionRef.current)
  }, [])

  // Terdeteksi dari polling meta.json milik ClearBrowserCacheBoundary
  useEffect(() => {
    if (!isLatestVersion) {
      markUpdateAvailable(latestVersion)
    }
  }, [isLatestVersion, latestVersion, markUpdateAvailable])

  // Polling library berhenti saat window blur, jadi cek langsung saat tab/window kembali aktif.
  // `focus` menangkap kembali dari window lain/DevTools/address bar (tab tetap visible);
  // `visibilitychange` menangkap kembali dari tab lain. Throttle mencegah fetch ganda.
  useEffect(() => {
    const checkLatestVersion = async () => {
      const now = Date.now()
      if (
        document.visibilityState !== 'visible' ||
        UNKNOWN_VERSIONS.includes(latestVersion) ||
        now - lastVisibleCheckRef.current < VISIBLE_CHECK_THROTTLE
      ) {
        return
      }
      lastVisibleCheckRef.current = now

      try {
        const version = await fetchLatestAppVersion()
        if (version && version !== latestVersion) {
          markUpdateAvailable(version)
        }
      } catch {
        // offline / meta.json gagal dimuat — polling berikutnya akan mencoba lagi
      }
    }

    document.addEventListener('visibilitychange', checkLatestVersion)
    window.addEventListener('focus', checkLatestVersion)
    return () => {
      document.removeEventListener('visibilitychange', checkLatestVersion)
      window.removeEventListener('focus', checkLatestVersion)
    }
  }, [latestVersion, markUpdateAvailable])

  // Chunk gagal dimuat di halaman input (lihat chunkReloadFallback)
  useEffect(() => {
    const handleChunkUpdate = async () => {
      let version = null
      try {
        version = await fetchLatestAppVersion()
      } catch {
        // versi tidak diketahui — reload nanti tetap jalan, hanya boot cek ulang sekali
      }
      markUpdateAvailable(version)
    }

    window.addEventListener(APP_UPDATE_EVENT, handleChunkUpdate)
    return () => {
      window.removeEventListener(APP_UPDATE_EVENT, handleChunkUpdate)
    }
  }, [markUpdateAvailable])

  // Pasang versi baru saat pindah halaman. history.listen jalan sebelum route baru dirender,
  // jadi chunk build lama (sudah 404 di server) tidak sempat diminta.
  useEffect(() => {
    if (!updateAvailable) {
      return undefined
    }

    return history.listen((nextLocation) => {
      if (isSafeToReloadPath(nextLocation.pathname)) {
        reloadPage()
      }
    })
  }, [updateAvailable, reloadPage])

  return { updateAvailable, isSafeToReload, pathname: location.pathname, onReloadPage: reloadPage }
}

export default useAppUpdate
